'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

const PARAM = '_toast';

export interface HandoffArrival {
  toast: string | null;
  dismiss: () => void;
}

/**
 * Reads the `?_toast=…` query param once on mount, scrubs it from the URL via
 * `router.replace`, and returns the decoded message. Intended to be mounted in
 * a top-level layout so any page the user lands on after a cross-product
 * handoff (F2 → F5) can render the arrival banner.
 */
export function useHandoffArrival(): HandoffArrival {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const fired = useRef(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const raw = params?.get(PARAM);
    if (!raw) return;

    setToast(raw);

    const remaining = new URLSearchParams(params?.toString() ?? '');
    remaining.delete(PARAM);
    const qs = remaining.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname);
  }, [params, pathname, router]);

  const dismiss = useCallback(() => setToast(null), []);

  return { toast, dismiss };
}
